import * as React from 'react';
import {
  forwardRef,
  ComponentProps,
  MouseEvent as ReactMouseEvent,
  useCallback,
  PropsWithChildren,
  useState,
  useEffect,
} from 'react';
import Image from 'next/image';
import { cn, customImageLoader } from '@restworld/utils-common';
import { focusRing, notificationIndicatorStyles } from '@restworld/ui-ds-base';

import { IconComponentType } from '../icon-button';
import { V2Link } from '../../feature-components';

// eslint-disable-next-line jsx-a11y/anchor-has-content
const ForwardedLink = forwardRef<HTMLAnchorElement, ComponentProps<'a'>>((props, ref) => <a ref={ref} {...props} />);
ForwardedLink.displayName = 'ForwardedLink';

export const NavMenuItem = forwardRef<HTMLAnchorElement, NavMenuItemProps>((props, ref) => {
  const {
    onClick,
    children,
    twStyle,
    Icon,
    isSelected,
    isOpen,
    imageUrl,
    indicator,
    noV2Prefix,
    newTab = false,
    href = '#!',
    isLink = true,
    ...restProps
  } = props;
  const [delayedIsOpen, setDelayedIsOpen] = useState(false);

  const handleClick = useCallback(
    (e: ReactMouseEvent<HTMLAnchorElement>) => {
      if (!isLink) e.preventDefault();
      if (onClick) onClick(e);
    },
    [isLink, onClick]
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    // the expansion on hover of the side nav causes a distortion in the nav item text as it might occupy 2 lines
    //  at the start then occupy one line when the expansion animation ends. This delay before showing the text accounts
    // for that, so users don't experience the text flickering.
    if (isOpen) {
      timeoutId = setTimeout(() => {
        setDelayedIsOpen(isOpen);
      }, 150);
    } else {
      clearTimeout(timeoutId);
      setDelayedIsOpen(!!isOpen);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  return (
    <StyledNavMenuItem
      ref={ref}
      href={href}
      $isSelected={isSelected}
      $isOpen={isOpen}
      onClick={handleClick}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      twStyle={twStyle}
      noV2Prefix={noV2Prefix}
      {...restProps}
    >
      {imageUrl ? (
        <ImageWrapper>
          {/* object-cover on the wrapper alone doesn't crop the inner <img> —
              next/image with `fill` stretches by default. Apply it on the
              Image itself so non-square avatars get cropped, not squashed. */}
          <Image
            alt=""
            src={imageUrl}
            fill
            loader={customImageLoader}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        </ImageWrapper>
      ) : Icon ? (
        <Icon className="h-6 w-6 min-w-[24px] max-w-[24px]" />
      ) : null}
      {delayedIsOpen ? (
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2">{children}</div>
          {typeof indicator === 'number' && <NotificationIndicator>{indicator}</NotificationIndicator>}
        </div>
      ) : null}
    </StyledNavMenuItem>
  );
});
NavMenuItem.displayName = 'NavMenuItem';

export default NavMenuItem;

export interface NavMenuItemProps extends ComponentProps<'a'>, PropsWithChildren<StyledNavMenuItemProps> {
  Icon?: IconComponentType;
  newTab?: boolean;
  imageUrl?: string;
  isLink?: boolean;
  indicator?: number;
  noV2Prefix?: boolean;
}
interface StyledNavMenuItemProps {
  isSelected?: boolean;
  isOpen?: boolean;
  twStyle?: string;
}

const StyledNavMenuItem = ({ $isSelected, $isOpen, twStyle, className, ...rest }: any) => (
  <V2Link
    className={cn(
      `flex items-center gap-2 no-underline text-inherit rounded-lg py-1 px-2 text-base min-h-[40px] font-medium transition-all duration-200 ease-in-out hover:cursor-pointer ${focusRing}`,
      $isSelected
        ? 'text-purple-700 hover:bg-purple-700/10 active:bg-purple-700/20 bg-purple-700/10'
        : 'text-neutral-700 hover:bg-neutral-900/10 active:bg-neutral-900/20',
      $isOpen ? 'w-full' : 'w-fit',
      twStyle,
      className
    )}
    {...rest}
  />
);
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'h-6 w-6 min-w-[24px] max-w-[24px] rounded-full overflow-hidden relative object-cover object-center',
      className
    )}
    {...props}
  />
);
const NotificationIndicator = ({ className, ...props }: any) => (
  <span className={cn(notificationIndicatorStyles, className)} {...props} />
);
